import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Gift, Upload, Users, TrendingUp, Calendar, CheckCircle, Clock, 
  AlertCircle, Cake, Phone, Mail, FileSpreadsheet, X, Search,
  Euro, Percent, ChevronRight, Loader2, PartyPopper, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Commission rates by plan
const COMMISSION_RATES = {
  free: 45,
  starter: 20,
  pro: 15,
  business: 10
};

export const BirthdayClubDashboard = ({ user }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [clubStatus, setClubStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsPage, setContactsPage] = useState(1);
  const [codes, setCodes] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Setup form
  const [setupForm, setSetupForm] = useState({
    offer_title: '',
    offer_description: '',
    discount_type: 'percentage',
    discount_value: 20,
    min_purchase: '',
    terms: ''
  });

  // Redeem form
  const [redeemForm, setRedeemForm] = useState({
    code: '',
    sale_amount: ''
  });
  const [redeeming, setRedeeming] = useState(false);

  const fetchClubStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/birthday-club/status`, {
        headers: { 'X-User-Id': user.id }
      });
      setClubStatus(data);
      
      if (!data.exists) {
        setShowSetupModal(true);
      }
    } catch (error) {
      console.error('Error fetching club status:', error);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  const fetchContacts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/birthday-club/contacts`, {
        headers: { 'X-User-Id': user.id },
        params: { page: contactsPage, limit: 50, search: searchQuery }
      });
      setContacts(data.contacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }, [user.id, contactsPage, searchQuery]);

  const fetchCodes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/birthday-club/codes`, {
        headers: { 'X-User-Id': user.id }
      });
      setCodes(data.codes);
    } catch (error) {
      console.error('Error fetching codes:', error);
    }
  }, [user.id]);

  const fetchRedemptions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/birthday-club/redemptions`, {
        headers: { 'X-User-Id': user.id }
      });
      setRedemptions(data);
    } catch (error) {
      console.error('Error fetching redemptions:', error);
    }
  }, [user.id]);

  useEffect(() => {
    fetchClubStatus();
  }, [fetchClubStatus]);

  useEffect(() => {
    if (clubStatus?.exists) {
      if (activeTab === 'contacts') fetchContacts();
      if (activeTab === 'codes') fetchCodes();
      if (activeTab === 'sales') fetchRedemptions();
    }
  }, [activeTab, clubStatus, fetchContacts, fetchCodes, fetchRedemptions]);

  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/birthday-club/setup`, {
        ...setupForm,
        min_purchase: setupForm.min_purchase ? parseFloat(setupForm.min_purchase) : null
      }, {
        headers: { 'X-User-Id': user.id }
      });
      
      toast.success('Club VIP creado. Pendiente de aprobación.');
      setShowSetupModal(false);
      fetchClubStatus();
    } catch (error) {
      toast.error('Error al crear el club');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    const formData = new FormData();
    formData.append('file', file);
    
    setUploading(true);
    
    try {
      const { data } = await axios.post(`${API}/birthday-club/contacts/import`, formData, {
        headers: { 
          'X-User-Id': user.id,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`${data.imported} contactos importados, ${data.updated} actualizados`);
      
      if (data.errors?.length > 0) {
        toast.warning(`${data.total_errors} errores encontrados`);
      }
      
      fetchClubStatus();
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar contactos');
    } finally {
      setUploading(false);
    }
  }, [user.id, fetchClubStatus, fetchContacts]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1
  });

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!redeemForm.code || !redeemForm.sale_amount) {
      toast.error('Completa todos los campos');
      return;
    }
    
    setRedeeming(true);
    
    try {
      const { data } = await axios.post(`${API}/birthday-club/redeem`, {
        code: redeemForm.code,
        sale_amount: parseFloat(redeemForm.sale_amount),
        business_id: user.id
      });
      
      toast.success(`Código canjeado. Venta: ${data.sale_amount}€ - Comisión: ${data.commission_amount}€`);
      setRedeemForm({ code: '', sale_amount: '' });
      fetchClubStatus();
      fetchCodes();
      fetchRedemptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código no válido');
    } finally {
      setRedeeming(false);
    }
  };

  const userPlan = user?.subscriptionPlan?.toLowerCase() || 'free';
  const commissionRate = COMMISSION_RATES[userPlan] || 45;

  if (loading && !clubStatus) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl shadow-lg">
            <Cake className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Club VIP de Cumpleaños</h1>
            <p className="text-sm text-muted-foreground">
              Fideliza clientes con ofertas exclusivas de 7 días
            </p>
          </div>
        </div>
        
        {clubStatus?.exists && (
          <div className={`px-4 py-2 rounded-full text-sm font-medium ${
            clubStatus.club.status === 'active' 
              ? 'bg-green-100 text-green-700' 
              : clubStatus.club.status === 'pending'
              ? 'bg-yellow-100 text-yellow-700'
              : 'bg-gray-100 text-gray-700'
          }`}>
            {clubStatus.club.status === 'active' && '✓ Activo'}
            {clubStatus.club.status === 'pending' && '⏳ Pendiente de aprobación'}
            {clubStatus.club.status === 'approved' && '💳 Pendiente de pago'}
            {clubStatus.club.status === 'rejected' && '✕ Rechazado'}
          </div>
        )}
      </div>

      {/* Pending Approval Banner */}
      {clubStatus?.exists && clubStatus.club.status === 'pending' && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4"
        >
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800 dark:text-yellow-200">
                Tu solicitud está siendo revisada
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Un administrador revisará tu Club VIP y te notificará el precio de instalación basado en tu lista de {clubStatus.stats?.contacts_count || 0} contactos.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Approved but not paid Banner */}
      {clubStatus?.exists && clubStatus.club.status === 'approved' && !clubStatus.club.installation_paid && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4"
        >
          <div className="flex items-start gap-3">
            <Euro className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800 dark:text-blue-200">
                ¡Aprobado! Precio de instalación: {clubStatus.club.installation_price}€
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Contacta con nosotros para completar el pago y activar tu Club VIP de Cumpleaños.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stats Cards - Only show when active */}
      {clubStatus?.exists && clubStatus.club.status === 'active' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Users}
            label="Contactos"
            value={clubStatus.stats?.contacts_count || 0}
            color="blue"
          />
          <StatCard
            icon={Cake}
            label="Próximos 7 días"
            value={clubStatus.stats?.upcoming_birthdays || 0}
            color="pink"
          />
          <StatCard
            icon={CheckCircle}
            label="Canjeados"
            value={clubStatus.stats?.codes_redeemed || 0}
            sublabel={`${clubStatus.stats?.redemption_rate || 0}% tasa`}
            color="green"
          />
          <StatCard
            icon={Euro}
            label="Ventas Totales"
            value={`${clubStatus.stats?.total_sales?.toFixed(2) || '0.00'}€`}
            sublabel={`Comisión: ${clubStatus.stats?.total_commission?.toFixed(2) || '0.00'}€`}
            color="purple"
          />
        </div>
      )}

      {/* Tab Navigation */}
      {clubStatus?.exists && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Resumen', icon: BarChart3 },
            { id: 'contacts', label: 'Contactos', icon: Users },
            { id: 'codes', label: 'Códigos', icon: Gift },
            { id: 'sales', label: 'Ventas', icon: Euro },
            { id: 'redeem', label: 'Canjear', icon: CheckCircle }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200'
              }`}
              data-testid={`birthday-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {/* Overview Tab */}
        {activeTab === 'overview' && clubStatus?.exists && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            {/* Current Offer */}
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-2xl p-6 border border-pink-200/50">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5 text-pink-500" />
                Tu Oferta de Cumpleaños
              </h3>
              <div className="space-y-3">
                <p className="text-lg font-bold">{clubStatus.club.offer_title}</p>
                <p className="text-muted-foreground">{clubStatus.club.offer_description}</p>
                <div className="flex items-center gap-4 pt-2">
                  <span className="px-3 py-1 bg-pink-500 text-white rounded-full text-sm font-bold">
                    {clubStatus.club.discount_type === 'percentage' 
                      ? `${clubStatus.club.discount_value}% OFF` 
                      : `${clubStatus.club.discount_value}€ OFF`}
                  </span>
                  {clubStatus.club.min_purchase && (
                    <span className="text-sm text-muted-foreground">
                      Compra mínima: {clubStatus.club.min_purchase}€
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Commission Info */}
            <div className="bg-card rounded-2xl p-6 border border-border/50">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Percent className="w-5 h-5 text-purple-500" />
                Tu Comisión
              </h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Plan {userPlan.charAt(0).toUpperCase() + userPlan.slice(1)}</p>
                  <p className="text-3xl font-bold text-purple-600">{commissionRate}%</p>
                  <p className="text-sm text-muted-foreground">por venta rastreada</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Comisión acumulada</p>
                  <p className="text-2xl font-bold text-green-600">{clubStatus.stats?.total_commission?.toFixed(2) || '0.00'}€</p>
                </div>
              </div>
            </div>

            {/* File Upload */}
            <div className="bg-card rounded-2xl p-6 border border-border/50">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-blue-500" />
                Importar Contactos
              </h3>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                  isDragActive 
                    ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20' 
                    : 'border-gray-300 hover:border-pink-400'
                }`}
              >
                <input {...getInputProps()} />
                {uploading ? (
                  <Loader2 className="w-10 h-10 mx-auto animate-spin text-pink-500" />
                ) : (
                  <FileSpreadsheet className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                )}
                <p className="text-sm text-muted-foreground">
                  {isDragActive 
                    ? 'Suelta el archivo aquí...' 
                    : 'Arrastra un archivo Excel o CSV, o haz clic para seleccionar'}
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Columnas requeridas: nombre, teléfono/móvil, email, fecha de cumpleaños
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <motion.div
            key="contacts"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar contactos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl"
              />
            </div>

            {/* Contacts List */}
            <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
              <div className="grid grid-cols-4 gap-4 p-4 bg-muted/50 text-sm font-medium">
                <div>Nombre</div>
                <div>Contacto</div>
                <div>Cumpleaños</div>
                <div>Estado</div>
              </div>
              <div className="divide-y divide-border/50">
                {contacts.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No hay contactos importados
                  </div>
                ) : (
                  contacts.map((contact) => (
                    <div key={contact.id} className="grid grid-cols-4 gap-4 p-4 items-center">
                      <div className="font-medium">{contact.name}</div>
                      <div className="text-sm">
                        {contact.phone && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="w-3 h-3" /> {contact.phone}
                          </div>
                        )}
                        {contact.email && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Mail className="w-3 h-3" /> {contact.email}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Cake className="w-4 h-4 text-pink-500" />
                        {contact.birthday}
                      </div>
                      <div>
                        {contact.total_redeemed > 0 ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            {contact.total_redeemed} canjes
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                            Sin canjes
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Codes Tab */}
        {activeTab === 'codes' && (
          <motion.div
            key="codes"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
              <div className="grid grid-cols-5 gap-4 p-4 bg-muted/50 text-sm font-medium">
                <div>Código</div>
                <div>Cliente</div>
                <div>Cumpleaños</div>
                <div>Expira</div>
                <div>Estado</div>
              </div>
              <div className="divide-y divide-border/50">
                {codes.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No hay códigos generados todavía
                  </div>
                ) : (
                  codes.map((code) => (
                    <div key={code.id} className="grid grid-cols-5 gap-4 p-4 items-center">
                      <div className="font-mono font-bold text-pink-600">{code.code}</div>
                      <div>{code.contact_name}</div>
                      <div className="text-sm text-muted-foreground">{code.birthday}</div>
                      <div className="text-sm text-muted-foreground">
                        {new Date(code.expires_at).toLocaleDateString()}
                      </div>
                      <div>
                        {code.redeemed ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1 w-fit">
                            <CheckCircle className="w-3 h-3" /> Canjeado
                          </span>
                        ) : new Date(code.expires_at) < new Date() ? (
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                            Expirado
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs flex items-center gap-1 w-fit">
                            <Clock className="w-3 h-3" /> Pendiente
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Sales Tab */}
        {activeTab === 'sales' && (
          <motion.div
            key="sales"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            {/* Summary */}
            {redemptions.summary && (
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4">
                  <p className="text-sm text-muted-foreground">Ventas Totales</p>
                  <p className="text-2xl font-bold text-green-600">{redemptions.summary.total_sales}€</p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4">
                  <p className="text-sm text-muted-foreground">Comisión Acumulada</p>
                  <p className="text-2xl font-bold text-purple-600">{redemptions.summary.total_commission}€</p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-4">
                  <p className="text-sm text-muted-foreground">Total Canjes</p>
                  <p className="text-2xl font-bold text-blue-600">{redemptions.summary.total_redemptions}</p>
                </div>
              </div>
            )}

            {/* Redemptions List */}
            <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
              <div className="grid grid-cols-5 gap-4 p-4 bg-muted/50 text-sm font-medium">
                <div>Código</div>
                <div>Fecha</div>
                <div>Venta</div>
                <div>Comisión %</div>
                <div>Comisión €</div>
              </div>
              <div className="divide-y divide-border/50">
                {redemptions.redemptions?.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No hay ventas registradas
                  </div>
                ) : (
                  redemptions.redemptions?.map((r) => (
                    <div key={r.id} className="grid grid-cols-5 gap-4 p-4 items-center">
                      <div className="font-mono text-pink-600">{r.code}</div>
                      <div className="text-sm">{new Date(r.redeemed_at).toLocaleDateString()}</div>
                      <div className="font-medium">{r.sale_amount}€</div>
                      <div className="text-muted-foreground">{(r.commission_rate * 100).toFixed(0)}%</div>
                      <div className="font-medium text-purple-600">{r.commission_amount.toFixed(2)}€</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Redeem Tab */}
        {activeTab === 'redeem' && clubStatus?.club?.status === 'active' && (
          <motion.div
            key="redeem"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div className="max-w-md mx-auto">
              <div className="bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-2xl p-6 border border-pink-200/50">
                <div className="text-center mb-6">
                  <PartyPopper className="w-12 h-12 mx-auto text-pink-500 mb-3" />
                  <h3 className="text-lg font-semibold">Canjear Código de Cumpleaños</h3>
                  <p className="text-sm text-muted-foreground">
                    Introduce el código del cliente y el importe de la venta
                  </p>
                </div>

                <form onSubmit={handleRedeem} className="space-y-4">
                  <div>
                    <Label>Código del Cliente</Label>
                    <Input
                      placeholder="CUMPLE-XXXXXX"
                      value={redeemForm.code}
                      onChange={(e) => setRedeemForm({...redeemForm, code: e.target.value.toUpperCase()})}
                      className="rounded-xl text-center font-mono text-lg"
                      data-testid="redeem-code-input"
                    />
                  </div>
                  
                  <div>
                    <Label>Importe de la Venta (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="50.00"
                      value={redeemForm.sale_amount}
                      onChange={(e) => setRedeemForm({...redeemForm, sale_amount: e.target.value})}
                      className="rounded-xl"
                      data-testid="redeem-amount-input"
                    />
                  </div>

                  {redeemForm.sale_amount && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Comisión ({commissionRate}%)</span>
                        <span className="font-medium text-purple-600">
                          {(parseFloat(redeemForm.sale_amount || 0) * (commissionRate / 100)).toFixed(2)}€
                        </span>
                      </div>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={redeeming}
                    className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-xl py-6"
                    data-testid="redeem-submit-btn"
                  >
                    {redeeming ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 mr-2" />
                        Canjear Código
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Setup Modal */}
      <AnimatePresence>
        {showSetupModal && !clubStatus?.exists && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-pink-500 to-purple-600 rounded-xl">
                    <Cake className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-bold">Crear Club VIP de Cumpleaños</h2>
                </div>
                <button onClick={() => setShowSetupModal(false)}>
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSetupSubmit} className="space-y-4">
                <div>
                  <Label>Título de la Oferta</Label>
                  <Input
                    placeholder="Ej: 20% de descuento en tu cumpleaños"
                    value={setupForm.offer_title}
                    onChange={(e) => setSetupForm({...setupForm, offer_title: e.target.value})}
                    className="rounded-xl"
                    required
                  />
                </div>

                <div>
                  <Label>Descripción</Label>
                  <textarea
                    placeholder="Describe tu oferta especial de cumpleaños..."
                    value={setupForm.offer_description}
                    onChange={(e) => setSetupForm({...setupForm, offer_description: e.target.value})}
                    className="w-full min-h-[100px] px-3 py-2 bg-background border border-input rounded-xl resize-none"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo de Descuento</Label>
                    <select
                      value={setupForm.discount_type}
                      onChange={(e) => setSetupForm({...setupForm, discount_type: e.target.value})}
                      className="w-full px-3 py-2 bg-background border border-input rounded-xl"
                    >
                      <option value="percentage">Porcentaje (%)</option>
                      <option value="fixed">Cantidad fija (€)</option>
                    </select>
                  </div>
                  <div>
                    <Label>Valor del Descuento</Label>
                    <Input
                      type="number"
                      value={setupForm.discount_value}
                      onChange={(e) => setSetupForm({...setupForm, discount_value: e.target.value})}
                      className="rounded-xl"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label>Compra Mínima (opcional)</Label>
                  <Input
                    type="number"
                    placeholder="Sin mínimo"
                    value={setupForm.min_purchase}
                    onChange={(e) => setSetupForm({...setupForm, min_purchase: e.target.value})}
                    className="rounded-xl"
                  />
                </div>

                <div>
                  <Label>Términos y Condiciones (opcional)</Label>
                  <textarea
                    placeholder="Ej: No acumulable con otras ofertas..."
                    value={setupForm.terms}
                    onChange={(e) => setSetupForm({...setupForm, terms: e.target.value})}
                    className="w-full min-h-[80px] px-3 py-2 bg-background border border-input rounded-xl resize-none"
                  />
                </div>

                {/* Commission Info */}
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
                  <p className="text-sm text-purple-800 dark:text-purple-200">
                    <strong>Comisión:</strong> {commissionRate}% sobre cada venta rastreada (Plan {userPlan})
                  </p>
                  <p className="text-xs text-purple-600 dark:text-purple-300 mt-1">
                    El precio de instalación se determinará según el número de contactos
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-xl py-6"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Crear Club VIP
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, sublabel, color }) => {
  const colors = {
    blue: 'from-blue-500 to-cyan-500',
    pink: 'from-pink-500 to-rose-500',
    green: 'from-green-500 to-emerald-500',
    purple: 'from-purple-500 to-pink-500'
  };

  return (
    <div className="bg-card rounded-2xl p-4 border border-border/50">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
      {sublabel && <p className="text-xs text-muted-foreground mt-1">{sublabel}</p>}
    </div>
  );
};

export default BirthdayClubDashboard;
